import type { ReviewOption } from '@iheartjane/menu';
import { FEELING_ICONS } from '@iheartjane/menu';
import type { ReactNode } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useFetcher } from 'react-router';

import { ClientOnly } from '@jane/bloom/components/clientOnly';
import type { ActionResult } from '@jane/bloom/types/remixAction';
import type {
  ReviewTagOption,
  ReviewTagOptions,
} from '@jane/bloom/viewModels/reviewTagOptions';

import { LeaveReviewModal } from './leaveReviewModal';
import type { OpenLeaveReviewArgs } from './types';

interface LeaveReviewContextValue {
  /** Whether Leave Review is available to this user; entry points render nothing otherwise. */
  isEnabled: boolean;
  /** Whether the modal can open: entry points wait for this instead of opening half-loaded. */
  isReady: boolean;
  openLeaveReview: (args: OpenLeaveReviewArgs) => void;
}

const LeaveReviewContext = createContext<LeaveReviewContextValue>({
  isEnabled: false,
  isReady: false,
  openLeaveReview: () => undefined,
});

export const useLeaveReview = () => useContext(LeaveReviewContext);

const toFeelingOption = ({ id, label }: ReviewTagOption): ReviewOption => {
  const Icon = FEELING_ICONS[label];
  return {
    icon: Icon ? <Icon color={null} size="md" /> : undefined,
    id,
    label,
  };
};

interface LeaveReviewProviderProps {
  children: ReactNode;
  /** Leave Review flag is on and the user is logged in. */
  enabled: boolean;
}

/**
 * Makes the Leave Review modal openable from anywhere below it. Loads the
 * review questions' options up front, so the modal never opens without them.
 */
export const LeaveReviewProvider = ({
  children,
  enabled,
}: LeaveReviewProviderProps) => {
  const fetcher = useFetcher<ActionResult<ReviewTagOptions>>();
  const hasRequestedRef = useRef(false);
  const [openArgs, setOpenArgs] = useState<OpenLeaveReviewArgs | null>(null);

  useEffect(() => {
    if (!enabled || hasRequestedRef.current) return;
    hasRequestedRef.current = true;
    fetcher.load('/api/product-reviews');
  }, [enabled, fetcher]);

  const tagOptions = fetcher.data?.success ? fetcher.data.payload : null;
  const options = useMemo(
    () =>
      tagOptions && {
        activityOptions: tagOptions.activities,
        feelingOptions: tagOptions.feelings.map(toFeelingOption),
      },
    [tagOptions]
  );
  const isReady = enabled && options !== null;

  const openLeaveReview = useCallback(
    (args: OpenLeaveReviewArgs) => {
      if (!isReady) return;
      setOpenArgs(args);
    },
    [isReady]
  );

  const value = useMemo(
    () => ({ isEnabled: enabled, isReady, openLeaveReview }),
    [enabled, isReady, openLeaveReview]
  );

  return (
    <LeaveReviewContext.Provider value={value}>
      {children}
      {options && openArgs && (
        <ClientOnly>
          {(isMobile) => (
            <LeaveReviewModal
              key={openArgs.product.id} // Remounts if opened for a different product while already open.
              activityOptions={options.activityOptions}
              feelingOptions={options.feelingOptions}
              isMobile={isMobile}
              moreToReview={openArgs.moreToReview ?? []}
              onClose={() => setOpenArgs(null)}
              product={openArgs.product}
              source={openArgs.source}
            />
          )}
        </ClientOnly>
      )}
    </LeaveReviewContext.Provider>
  );
};
